import { FC, useMemo, useRef, useState } from 'react';
import { Controller, FieldError, FormProvider, useController, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  AutocompleteRenderInputParams,
  Box,
  DatePicker,
  FormInputText,
  InputLabel,
  Modal,
  PhoneNumberInput,
  ProfileImage,
  Select,
  Svg,
  SvgBubble,
  Text,
} from '@hopskipdrive/react-components';
import { useAuthQuery } from '@rideiq/auth/hooks/useAuthQuery';
import { Button } from '@rideiq/shared/components/Button';
import { colorTokens } from '@rideiq/shared/components/variables';
import { differenceInYears } from 'date-fns';
import { merge } from 'lodash';

import { analyticsClickUploadPhoto } from '../../../api/services/Analytics';
import { validateRiderDetails } from '../../../api/services/Riders';
import { useFlags } from '../../../contexts/LDContext';
import { useAgeRestrictionPolicy } from '../../../hooks/useAgeRestrictionPolicy';
import { useCategories } from '../../../hooks/useCategories';
import { useGenders } from '../../../hooks/useGenders';
import { useToastStore } from '../../../shared/stores/toastStore';
import { setFormErrorsForRiderProfile } from '../../../utils/helpers/setFormErrorsForRiderProfile';
import { DuplicateRiderModal } from '../../components/DuplicateRiderModal';
import { MIN_CAR_SEAT_AGE } from '../../constants/riderServices';
import { AideStatus } from '../../features/RiderAideStatus';
import { InvoiceGroupFormField } from '../InvoiceGroupDropdown';
import {
  ConfirmationModal,
  SharedBilling,
  sharedBillingValidationSchema,
  sharedBillingValidationSchemaV2,
} from '../SharedBilling';

import {
  ContactPhoto,
  FormField,
  FormRow,
  FullNameError,
  HalfFormField,
  HorizontalDivider,
  ImageWithIcon,
  RiderContact,
  UploadImage,
} from './RiderProfileForm.styles';
import { IRiderProfileFormType } from './types';
import { riderProfileSchema, riderProfileSchemaV2 } from './validationSchema';

interface RiderProfileFormProps {
  riderDetails: IRiderProfileFormType;
  onSubmit: (formData) => void;
  setIsSubmitting: (isSubmitting: boolean) => void;
  pauseState?: boolean;
  mustBeMetState?: boolean;
}

export const RiderProfileForm: FC<RiderProfileFormProps> = ({
  riderDetails,
  onSubmit,
  setIsSubmitting,
  pauseState,
  mustBeMetState,
}) => {
  const { isAdminOrOrganizerUser } = useAuthQuery();
  const { addToast } = useToastStore();
  const [fullNameError, setFullNameError] = useState('');
  const { enableApprovalFlowForSharedRider } = useFlags();
  const schema = isAdminOrOrganizerUser ? riderProfileSchemaV2(isAdminOrOrganizerUser) : riderProfileSchema();
  const shareBillingSchema = enableApprovalFlowForSharedRider
    ? sharedBillingValidationSchemaV2()
    : sharedBillingValidationSchema();

  const methods = useForm<RiderFormType>({
    defaultValues: useMemo(() => {
      return {
        ...riderDetails,
        ...(riderDetails.invoiceGroup && { invoiceGroupId: riderDetails.invoiceGroup.id }),
        ...(riderDetails?.sharedBillingRider
          ? {
              isSharedBillingRider: true,
              sharedBillingRider: {
                residenceAccountId: riderDetails.sharedBillingRider.residenceAccountId,
                residenceAccountContactName: riderDetails.sharedBillingRider.residenceAccountContactName,
                residenceAccountContactId: riderDetails.sharedBillingRider.residenceAccountContactId,
                residenceAccountContactEmail: riderDetails.sharedBillingRider.residenceAccountContactEmail,
              },
            }
          : {
              isSharedBillingRider: false,
              sharedBillingRider: {
                residenceAccountId: null,
                residenceAccountContactName: '',
                residenceAccountContactEmail: '',
              },
            }),
      };
    }, [riderDetails]),
    resolver: yupResolver(schema.concat(shareBillingSchema)),
    mode: 'onBlur',
    shouldUnregister: true,
  });
  const {
    control,
    setError,
    handleSubmit,
    formState: { errors },
  } = methods;
  const { categories } = useCategories();
  const { genders } = useGenders();
  const [formData, setFormData] = useState<RiderFormType | null>(null);
  const [showAgeRestrictionModal, setShowAgeRestrictionModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [ageRestrictionMessage, setAgeRestrictionMessage] = useState<string | null>(null);

  const { getAgeRestrictionPolicyMessage } = useAgeRestrictionPolicy();

  const handleSaveRider = async (allData: RiderFormType, bypassAgeCheck = false) => {
    // this will set the modal button to disabled while validating, avoiding any spam clicking
    setIsSubmitting(true);
    // if there is an existing photo and the form object has a new attachment, destroy existing photo
    setFormData(allData);
    const { sharedBillingRider, isSharedBillingRider, ...data } = allData;

    if (riderDetails?.photoId && data.photosAttributes[0].attachment?.name) {
      const attributes = data.photosAttributes[0] || {};

      data.photosAttributes[0] = merge(attributes, { _destroy: '', id: riderDetails.photoId.toString() });
    }
    const riderAge = data.dateOfBirth && differenceInYears(Date.now(), new Date(data.dateOfBirth));

    let photoRemoved = false;

    // destroy photo
    if (!data.photosAttributes[0].attachment && riderDetails?.photoId) {
      const attributes = data.photosAttributes[0] || {};

      data.photosAttributes[0] = merge(attributes, { _destroy: '1', id: riderDetails.photoId.toString() });
      photoRemoved = true;
    }

    if (riderAge !== null && riderAge < MIN_CAR_SEAT_AGE && !bypassAgeCheck) {
      setAgeRestrictionMessage(getAgeRestrictionPolicyMessage(riderAge));
      setShowAgeRestrictionModal(true);

      return false;
    }

    const { photosAttributes, ...dataToValidate } = data;

    const response = await validateRiderDetails({
      ...dataToValidate,
      ...(photoRemoved
        ? {}
        : { photosAttributes: photosAttributes.map((photo) => ({ attachment: photo.attachment })) }),
      ...(isSharedBillingRider ? { sharedBillingRider } : {}),
    });

    if (response.type === 'success') {
      setShowConfirmationModal(true);

      return false;
    } else if (response.type === 'error') {
      const validationErrors = response.data?.errors;

      validationErrors && setFormErrorsForRiderProfile({ setError, validationErrors, setFullNameError, addToast });
      setIsSubmitting(false);

      return false;
    }

    onChecksSuccess(allData);

    return true;
  };

  const onChecksSuccess = (allData) => {
    const { sharedBillingRider, isSharedBillingRider, ...data } = allData;

    onSubmit({
      ...data,
      ...(isSharedBillingRider
        ? {
            sharedBillingRider: {
              residenceAccountContactEmail: sharedBillingRider?.residenceAccountContactEmail,
              ...(enableApprovalFlowForSharedRider
                ? { residenceAccountContactId: sharedBillingRider?.residenceAccountContactId }
                : { residenceAccountContactName: sharedBillingRider?.residenceAccountContactName }),
              residenceAccountId: sharedBillingRider?.residenceAccountId,
              confirmed: true,
            },
          }
        : {}),
      ...(!isSharedBillingRider
        ? riderDetails?.sharedBillingRider && {
            sharedBillingRider: {
              inactive: true,
            },
          }
        : {}),
    });
  };

  return (
    <FormProvider {...methods}>
      <form id="rider-basic-info" aria-label="form" onSubmit={handleSubmit((data) => handleSaveRider(data))}>
        <Box display="flex" flexDirection="column" alignItems="flex-start" gap="1.5rem">
          <FormRow>
            <FormField>
              <InputLabel fieldtype="required" htmlFor="firstName">
                First name
              </InputLabel>
              <Controller
                control={control}
                name="firstName"
                render={({ field: { value, onChange, onBlur } }) => (
                  <FormInputText
                    variant="outlined"
                    name="firstName"
                    value={value}
                    data-testid="firstName"
                    fullWidth
                    error={fullNameError ? !!(errors.firstName && errors.lastName) : !!errors.firstName}
                    helperText={fullNameError ? '' : errors.firstName?.message}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                )}
              />
            </FormField>

            <FormField>
              <InputLabel fieldtype="required" htmlFor="lastName">
                Last name
              </InputLabel>
              <Controller
                control={control}
                name="lastName"
                render={({ field: { value, onChange, onBlur } }) => (
                  <FormInputText
                    variant="outlined"
                    name="lastName"
                    data-testid="lastName"
                    fullWidth
                    value={value}
                    error={fullNameError ? !!(errors.firstName && errors.lastName) : !!errors.lastName}
                    helperText={fullNameError ? '' : errors.lastName?.message}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                )}
              />
            </FormField>
          </FormRow>

          {fullNameError && (
            <FullNameError>
              <Text textColor="error">{fullNameError}</Text>
            </FullNameError>
          )}

          <FormRow>
            <FormField>
              <InputLabel fieldtype="required" htmlFor="dateOfBirth">
                Date of birth
              </InputLabel>
              <Controller
                control={control}
                name="dateOfBirth"
                render={({ field: { onChange, value } }) => (
                  <DatePicker
                    onChange={onChange}
                    value={value}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.dateOfBirth,
                        helperText: errors.dateOfBirth?.message,
                        inputProps: {
                          'data-testid': 'date-of-birth-picker',
                        },
                      },
                    }}
                  />
                )}
              />
            </FormField>

            <FormField>
              <InputLabel fieldtype="required" htmlFor="gender">
                Gender
              </InputLabel>
              <Controller
                control={control}
                name="gender"
                defaultValue={genders[0]?.value}
                render={({ field: { value, onChange } }) => (
                  <Select
                    options={genders}
                    value={genders.length ? value : undefined}
                    setValue={onChange}
                    renderInput={(params: AutocompleteRenderInputParams) => (
                      <FormInputText
                        {...params}
                        variant="outlined"
                        fullWidth
                        data-testid="gender"
                        error={!!errors.gender}
                        helperText={errors.gender?.message}
                      />
                    )}
                  />
                )}
              />
            </FormField>
          </FormRow>

          <HalfFormField>
            <InputLabel fieldtype="required" htmlFor="category">
              Category
            </InputLabel>
            <Controller
              control={control}
              name="category"
              defaultValue={categories[0]?.value}
              render={({ field: { value, onChange } }) => (
                <Select
                  options={categories}
                  value={categories.length ? value : undefined}
                  setValue={onChange}
                  renderInput={(params: AutocompleteRenderInputParams) => (
                    <FormInputText
                      {...params}
                      variant="outlined"
                      data-testid="category-form"
                      fullWidth
                      error={!!errors.category}
                      helperText={errors.category?.message}
                    />
                  )}
                />
              )}
            />
          </HalfFormField>

          <HorizontalDivider />
          <AideStatus isPaused={pauseState} isMustBeMet={mustBeMetState} />
          <HorizontalDivider />

          <ContactPhoto>
            <RiderContact>
              <Box>
                <InputLabel htmlFor="mobilePhoneNumber">Phone number</InputLabel>
                <Controller
                  control={control}
                  name="mobilePhoneNumber"
                  render={({ field: { onChange, value, name } }) => (
                    <PhoneNumberInput
                      variant="outlined"
                      name={name}
                      fullWidth
                      data-testid={name}
                      onValueChange={onChange}
                      initialValue={value}
                      error={!!errors.mobilePhoneNumber}
                      helperText={errors.mobilePhoneNumber?.message}
                    />
                  )}
                />
              </Box>
              <Box>
                <InputLabel htmlFor="email">Email</InputLabel>
                <Controller
                  control={control}
                  name="email"
                  render={({ field: { value, onChange, onBlur } }) => (
                    <FormInputText
                      variant="outlined"
                      name="email"
                      data-testid="email"
                      fullWidth
                      value={value}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      onChange={onChange}
                      onBlur={onBlur}
                    />
                  )}
                />
              </Box>
            </RiderContact>
            <FileInput
              control={control}
              riderDetails={riderDetails}
              errorMessage={
                errors.photosAttributes && (errors.photosAttributes[0]?.attachment as unknown as FieldError)?.message
              }
            />
          </ContactPhoto>

          <HorizontalDivider />

          <HalfFormField>
            <InputLabel htmlFor="externalId">ID number</InputLabel>
            <Controller
              control={control}
              name="externalId"
              render={({ field: { onChange, value } }) => (
                <FormInputText
                  variant="outlined"
                  name="externalId"
                  data-testid="idNumber"
                  fullWidth
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          </HalfFormField>
          {isAdminOrOrganizerUser && (
            <HalfFormField>
              <InvoiceGroupFormField
                control={control}
                initialValue={riderDetails.invoiceGroup}
                error={!!errors.invoiceGroupId}
                helperText={errors.invoiceGroupId?.message as string}
              />
            </HalfFormField>
          )}
          <HorizontalDivider />
          <SharedBilling riderId={riderDetails?.slug} />
        </Box>
        {showAgeRestrictionModal && (
          <Modal open={showAgeRestrictionModal}>
            <Modal.Content>{ageRestrictionMessage}</Modal.Content>
            <Modal.Actions>
              <Button
                color="primary"
                autoFocus
                onClick={() => {
                  setShowAgeRestrictionModal(false);
                  formData && handleSaveRider(formData, true);
                }}
              >
                Ok
              </Button>
            </Modal.Actions>
          </Modal>
        )}
      </form>
      <DuplicateRiderModal slug={riderDetails.slug} />
      <ConfirmationModal
        currentRider={{
          slug: riderDetails?.slug,
          isSharedBillingRider: !!riderDetails?.sharedBillingRider,
          sharedBillingRider: riderDetails?.sharedBillingRider,
        }}
        needsConfirmation={showConfirmationModal}
        onConfirm={() => {
          setShowConfirmationModal(false);
          formData && onChecksSuccess(formData);
        }}
        onCancel={() => {
          setShowConfirmationModal(false);
          setIsSubmitting(false);
        }}
        riderStatus={riderDetails?.sharedBillingRider?.status}
      />
    </FormProvider>
  );
};

const FileInput = ({ control, errorMessage, riderDetails }) => {
  const { field } = useController({ control, name: 'photosAttributes.0.attachment' });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  const [preview, setPreview] = useState(riderDetails.photoId ? riderDetails.profilePhotoUrl : '');
  const PHOTO_SIZE_LIMIT = 10e6;

  return (
    <Box display="flex" gap="1rem" alignItems="center">
      {preview ? (
        <ImageWithIcon>
          <ProfileImage size={72} imgSrc={preview} />
          <SvgBubble
            height="25px"
            width="25px"
            bgColor={colorTokens.background.default}
            onClick={() => {
              setValue('');
              setPreview('');
              field.onChange('');
            }}
            data-testid="trashcan"
          >
            <Svg icon="trashcan" width="14px" />
          </SvgBubble>
        </ImageWithIcon>
      ) : (
        <Svg width="72px" height="72px" icon="profile-avatar" />
      )}
      <UploadImage>
        <Button
          color="secondary"
          size="small"
          onClick={() => {
            analyticsClickUploadPhoto('Edit Rider');
            fileInputRef?.current?.click();
          }}
        >
          {'Upload New Photo'}
          <input
            type="file"
            accept=".png,.jpg,.jpeg"
            ref={fileInputRef}
            hidden
            value={value}
            data-testid="image-upload"
            onChange={(e) => {
              setValue(e.target.value);
              if (e.target?.files && e.target?.files[0]) {
                const photoUpload = e.target.files[0];

                setPreview(URL.createObjectURL(photoUpload));

                if (photoUpload.size > PHOTO_SIZE_LIMIT) {
                  // this onChange trigger schema validation
                  field.onChange(e.target.files);
                } else {
                  field.onChange(e.target.files[0]);
                }
                field.onBlur();
              }
            }}
          />
        </Button>
        <p>Must be a JPG or PNG less than 10MB in size.</p>
        {errorMessage && <Text textColor="error">{errorMessage}</Text>}
      </UploadImage>
    </Box>
  );
};
